/**
 * Centralized plan utilities for consistent naming across the application.
 * Display names, keys, and Stripe IDs come from the single source of truth
 * in `@/config/plans.shared`. This file only adds React/Tailwind concerns
 * (badge styling, Badge component wrappers).
 */

import { Badge } from '@/components/ui/badge';
import {
  PLAN_KEYS as SHARED_PLAN_KEYS,
  PLAN_DISPLAY_NAMES as SHARED_PLAN_DISPLAY_NAMES,
  getPlanDisplayName as sharedGetPlanDisplayName,
  type PlanKey,
} from '@/config/plans.shared';

export type PlanType = PlanKey;

/** Get the display name for a plan */
export const getPlanDisplayName = (plan: string | null): string =>
  sharedGetPlanDisplayName(plan);

/** Get badge styling classes for a plan (admin context - dark theme) */
export const getPlanBadgeStyleAdmin = (plan: string | null): string => {
  switch (plan) {
    case 'super':
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    case 'wonder':
      return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'captain':
      return 'bg-slate-500/30 text-slate-200 border-slate-400/40';
    case 'free':
    default:
      return 'bg-slate-600/30 text-slate-300 border-slate-500/40';
  }
};

/** Get badge styling classes for a plan (user context - light/dark theme) */
export const getPlanBadgeStyleUser = (plan: string | null): string => {
  switch (plan) {
    case 'super':
      return 'bg-primary/20 text-primary border-primary/30';
    case 'wonder':
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    case 'captain':
      return 'bg-slate-600 text-white border-slate-500';
    case 'free':
    default:
      return 'bg-muted/50 text-muted-foreground border-muted';
  }
};

/** All valid plan keys (re-export from shared) */
export const PLAN_KEYS: PlanType[] = [...SHARED_PLAN_KEYS];

/** Plan labels mapping for UI display (re-export from shared) */
export const planLabels: Record<string, string> = { ...SHARED_PLAN_DISPLAY_NAMES };

/** Get a styled Badge component for a plan (admin context) */
export const getPlanBadgeAdmin = (plan: string | null) => (
  <Badge className={getPlanBadgeStyleAdmin(plan)}>
    {getPlanDisplayName(plan)}
  </Badge>
);

/** Get a styled Badge component for a plan (user context) */
export const getPlanBadgeUser = (plan: string | null) => (
  <Badge className={getPlanBadgeStyleUser(plan)}>
    {getPlanDisplayName(plan)}
  </Badge>
);
